/**
 * Service pour la gestion des campagnes (menus & événements) d'un commerce.
 *
 * Fournit des méthodes pour :
 * - list : lister les campagnes d'un commerce, avec filtres optionnels.
 * - getById : récupérer une campagne par son identifiant.
 * - create : créer une nouvelle campagne.
 * - update : mettre à jour une campagne existante.
 * - remove : supprimer (soft delete) une campagne.
 * - setTargets : remplacer les cibles (produits/catégories/promotions) d'une campagne.
 * - publish / pause : changer le statut d'une campagne.
 * - listMedia / removeMedia / reorderGalleryMedia : gérer les médias d'une campagne
 *   (l'upload multipart suit un pattern différent, voir CampaignMediaPanel.vue).
 *
 * Utilise axiosInstance pour les requêtes HTTP avec interceptors configurés.
 * Toutes les réponses de cette API sont enveloppées en { data, message } —
 * chaque méthode ci-dessous dépaquette et retourne directement le payload.
 */
import axios, { type AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import { token } from '@/refs/common.refs';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import type {
  ICampaign,
  ICampaignMedia,
  ICampaignMediaRole,
  ICampaignStatus,
  ICampaignTargets,
  IUpdateCampaignBody,
} from '@/components/commerces/campaigns/campaign.types';

interface IApiEnvelope<T> {
  data: T;
  message: string;
}

export interface IListCampaignsParams {
  status?: ICampaignStatus[];
  visibleOnly?: boolean;
}

export interface IListCampaignMediaParams {
  role?: ICampaignMediaRole;
  activeOnly?: boolean;
}

function buildListQuery(params?: IListCampaignsParams): string {
  const search = new URLSearchParams();
  params?.status?.forEach((s) => search.append('status', s));
  if (params?.visibleOnly !== undefined) {
    search.set('visibleOnly', String(params.visibleOnly));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

function buildMediaListQuery(params?: IListCampaignMediaParams): string {
  const search = new URLSearchParams();
  if (params?.role) search.set('role', params.role);
  if (params?.activeOnly !== undefined) {
    search.set('activeOnly', String(params.activeOnly));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export const CampaignService = {
  list: async (storeId: string, params?: IListCampaignsParams) => {
    try {
      const response = await axiosInstance.get<IApiEnvelope<ICampaign[]>>(
        `stores/${storeId}/campaigns${buildListQuery(params)}`,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  getById: async (storeId: string, id: string) => {
    try {
      const response = await axiosInstance.get<IApiEnvelope<ICampaign>>(
        `stores/${storeId}/campaigns/${id}`,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  /**
   * Récupère la campagne dans les 3 langues admin (fr/en/nl). name/description
   * sont résolus côté serveur selon accept-language — axiosInstance impose
   * systématiquement la langue courante de l'admin dans son intercepteur, donc
   * ces 3 appels passent par axios brut avec un accept-language explicite par
   * requête, seul moyen de reconstituer les 3 traductions pour le formulaire.
   */
  getByIdInAllLanguages: async (
    storeId: string,
    id: string,
  ): Promise<Record<'fr' | 'en' | 'nl', ICampaign>> => {
    const langs = ['fr', 'en', 'nl'] as const;
    const results = await Promise.all(
      langs.map((lang) =>
        axios.get<IApiEnvelope<ICampaign>>(
          `${VITE_API_URL}stores/${storeId}/campaigns/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token.value?.accessToken}`,
              'x-client-app': 'admin-back-office',
              'accept-language': lang,
            },
          },
        ),
      ),
    );
    return {
      fr: results[0].data.data,
      en: results[1].data.data,
      nl: results[2].data.data,
    };
  },

  create: async (storeId: string, body: IUpdateCampaignBody) => {
    try {
      const response = await axiosInstance.post<IApiEnvelope<ICampaign>>(
        `stores/${storeId}/campaigns`,
        body,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  update: async (storeId: string, id: string, body: IUpdateCampaignBody) => {
    try {
      const response = await axiosInstance.put<IApiEnvelope<ICampaign>>(
        `stores/${storeId}/campaigns/${id}`,
        body,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  remove: async (storeId: string, id: string) => {
    try {
      const response = await axiosInstance.delete(
        `stores/${storeId}/campaigns/${id}`,
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  setTargets: async (
    storeId: string,
    id: string,
    targets: Partial<ICampaignTargets>,
  ) => {
    try {
      const response = await axiosInstance.put<IApiEnvelope<ICampaign>>(
        `stores/${storeId}/campaigns/${id}/targets`,
        targets,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  publish: async (storeId: string, id: string) => {
    try {
      const response = await axiosInstance.post<IApiEnvelope<ICampaign>>(
        `stores/${storeId}/campaigns/${id}/publish`,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  pause: async (storeId: string, id: string) => {
    try {
      const response = await axiosInstance.post<IApiEnvelope<ICampaign>>(
        `stores/${storeId}/campaigns/${id}/pause`,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  listMedia: async (
    storeId: string,
    campaignId: string,
    params?: IListCampaignMediaParams,
  ) => {
    try {
      const response = await axiosInstance.get<IApiEnvelope<ICampaignMedia[]>>(
        `stores/${storeId}/campaigns/${campaignId}/media${buildMediaListQuery(params)}`,
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  removeMedia: async (storeId: string, campaignId: string, mediaId: string) => {
    try {
      const response = await axiosInstance.delete(
        `stores/${storeId}/campaigns/${campaignId}/media/${mediaId}`,
      );
      return response.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },

  reorderGalleryMedia: async (
    storeId: string,
    campaignId: string,
    orderedIds: string[],
  ) => {
    try {
      const response = await axiosInstance.put<IApiEnvelope<ICampaignMedia[]>>(
        `stores/${storeId}/campaigns/${campaignId}/media/gallery/order`,
        { orderedIds },
      );
      return response.data.data;
    } catch (error) {
      return Promise.reject(error as AxiosError);
    }
  },
};
